import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContentCard from '../../../Common/Card';
import Icons from '../../../../../components/Common/Icons/Icons';
import MenuIcon from '../../../Layout/Menu/MenuIcon';
import { cn } from '../../../../../utils/styles';
import { IAccountData } from '../../../Layout/Account/Data';
import { MenuPopoverContent, Popover, PopoverTrigger } from '../../../../../components/Common/UI/Popover';
import Link from '../../../../../components/Common/UI/Link';
import FakePagination from '../../../../../components/Common/UI/FakePagination';
import { IAccountProps, setAccountInStorage, useAccountContextProvider } from '../../../../context/account';

interface IAccounts {
  title: string;
  iconStyle?: string;
  data: IAccountData[];
  icon: React.ReactNode;
  accountStyle: string;
}

const clickableDivProps: React.HTMLAttributes<HTMLDivElement | HTMLButtonElement> = {
  role: 'button',
  tabIndex: -1,
  'aria-label': 'select-container',
  onClick: e => e.stopPropagation(),
};

function Accounts({ title, iconStyle, data, icon, accountStyle }: IAccounts) {
  const navigate = useNavigate();
  const { handeUpdateAccount } = useAccountContextProvider();
  const handleSetAccount = (payload: IAccountProps) => {
    handeUpdateAccount(payload);
    setAccountInStorage(payload);
  };

  return (
    <ContentCard
      title={title}
      titleSize="small"
      icon={
        <MenuIcon icon={icon} className={cn('bg-primary border-primary', iconStyle)} variant="white" size="large" />
      }
    >
      {data.map(accData => (
        <div className="grid gap-x-[20px] lg:gap-y-[30px] gap-y-[20px] sm:grid-cols-2" key={accData.id}>
          {accData.accounts.map(account => (
            <div
              key={account.id}
              className={cn([
                'grid grid-cols-[1fr,auto] gap-[10px] items-start border bg-secondary-3 ',
                'border-l-[5px] h-[65px] p-[10px] rounded-[10px] cursor-pointer',
                'hover:bg-secondary-7',
                accountStyle,
              ])}
              onClick={() => {
                // Scroll to top before changing account
                const documentEl = window.document.getElementById('root');

                documentEl?.scrollIntoView({ behavior: 'smooth' });
                //

                handleSetAccount({ name: account.title, number: account.number });

                if (accData.type === 'mortgage') {
                  navigate('/guidance/statements/mortgage-statements');
                } else if (accData.type === 'visa') {
                  navigate('/guidance/statements/visa-classic');
                } else {
                  navigate('/guidance/statements/checking-statements');
                }
              }}
              role="button"
              tabIndex={-1}
            >
              <div>
                <div className="font-medium text-[18px]">{account.number}</div>
                <div className="text-xs opacity-50">{account.title}</div>
              </div>
              <Popover>
                <PopoverTrigger className="block" {...clickableDivProps}>
                  <Icons.More className="[&_path]:fill-secondary2 rotate-90" />
                </PopoverTrigger>
                <MenuPopoverContent className="!bg-white">
                  <div className="text-center" {...clickableDivProps}>
                    <div className="text-xs leading-[20px]">{account.number}</div>
                    <div className="opacity-50 text-[10px]">{account.title}</div>
                    <Link to="/guidance/profile" className="text-addition6 font-bold underline text-[10px]">
                      Go To Account Preferences
                    </Link>
                  </div>
                </MenuPopoverContent>
              </Popover>
            </div>
          ))}
        </div>
      ))}
      <div className="flex justify-end mt-[25px]">
        <FakePagination value="1 - 1 of 1" disabled />
      </div>
    </ContentCard>
  );
}

Accounts.defaultProps = {
  iconStyle: '',
};

export default Accounts;
